import moment from 'moment';
import Mailcheck from 'mailcheck';

/*
 * Validators
 *
 * Meant to be used with Mobx form. These functions can be
 * supplied as the "validator" functions
 *
 * https://foxhound87.github.io/mobx-react-form/docs/validation/plugins/VJF/extend.html
 *
 *
 */

// TODO-TS: Expand mobx-react-form typings
interface FieldType {
  error: any;
  isValid: boolean;
  label: string;
  value: any;
}

interface TestingFieldType {
  value: string;
}

interface FormType {
  $: (key: string) => any;
}

interface EmailValidatorParams {
  field: FieldType | TestingFieldType;
}
type EmailValidatorType = ({
  field,
}: EmailValidatorParams) => [boolean, string];

export function match(target: string) {
  return ({
    field,
    form,
  }: {
    field: FieldType;
    form: FormType;
  }): [boolean, string] => {
    const targetField = form.$(target);
    const fieldsAreEquals = targetField.value === field.value;
    return [
      fieldsAreEquals,
      `${field.label} must match ${targetField.label.toLowerCase()}.`,
    ];
  };
}

export function shouldBePresentWith(target: string) {
  return ({
    form,
    field,
  }: {
    field: FieldType;
    form: FormType;
  }): [boolean, string] => {
    const targetField = form.$(target);
    const isInvalid = targetField.value !== '' && field.value === '';
    return [
      !isInvalid,
      `The ${field.label} field is required when ${targetField.label} is not empty.`,
    ];
  };
}

// Checks to see if field has been touched
// before determining if it is valid.
export function fieldIsValid(field: FieldType): boolean {
  // Field hasn't been touched
  if (field.error === null) {
    return true;
  }

  return field.isValid;
}

// Checks to see if message is non-empty.
export function validateNonEmptyMessage(message: string): boolean {
  return /^\s*$/.test(message);
}

// Checks to see if field is a date
export function validateDate({
  field,
}: {
  field: FieldType;
}): [boolean, string] {
  return [
    moment(field.value, 'MM/DD/YYYY', true).isValid(),
    `The ${field.label} field is is not valid.`,
  ];
}

// Checks to see if field is before another date
export function validateDateBefore(
  date: moment.MomentInput,
  limit: moment.unitOfTime.StartOf,
) {
  return ({ field }: { field: FieldType }): [boolean, string] => [
    moment(field.value, 'MM/DD/YYYY', true).isBefore(date, limit),
    `The ${field.label} field is is not valid.`,
  ];
}

// Checks to see if field is after another date
export function validateDateAfter(
  date: moment.MomentInput,
  limit: moment.unitOfTime.StartOf,
) {
  return ({ field }: { field: FieldType }): [boolean, string] => [
    moment(field.value, 'MM/DD/YYYY', true).isAfter(date, limit),
    `The ${field.label} field is is not valid.`,
  ];
}

// Checked against https://wiki.mozilla.org/TLD_List
const TLD_BLACKLIST = [
  'ccom',
  'cim',
  'ckm',
  'clm',
  'cmo',
  'coim',
  'col',
  'comn',
  'comg',
  'comj',
  'comp',
  'comb',
  'coma',
  'come',
  'con',
  'conm',
  'coom',
  'copm',
  'cpm',
  'cvom',
  'du',
  'eud',
  'esu',
  'fom',
  'ocm',
  'ort',
  'vom',
  'xom',
];

const domains = [
  'msn.com',
  'bellsouth.net',
  'telus.net',
  'comcast.net',
  'optusnet.com.au',
  'earthlink.net',
  'qq.com',
  'sky.com',
  'icloud.com',
  'mac.com',
  'sympatico.ca',
  'googlemail.com',
  'att.net',
  'xtra.co.nz',
  'web.de',
  'cox.net',
  'gmail.com',
  'ymail.com',
  'aim.com',
  'rogers.com',
  'verizon.net',
  'rocketmail.com',
  'google.com',
  'optonline.net',
  'sbcglobal.net',
  'aol.com',
  'me.com',
  'btinternet.com',
  'charter.net',
  'shaw.ca',
];

const secondLevelDomains = [
  'yahoo',
  'hotmail',
  'gmail',
  'mail',
  'live',
  'outlook',
  'gmx',
];

export const TLD_REG_EXP = new RegExp(`\\.(${TLD_BLACKLIST.join('|')})$`);

export const validateEmailTLD: EmailValidatorType = ({ field }) => {
  const email = (field.value as string) || '';
  const regexMatch = email.match(TLD_REG_EXP);
  return [
    !regexMatch,
    `Email ending in '${regexMatch && regexMatch[0]}' is invalid.`,
  ];
};

interface EmailSuggestion {
  address: string;
  domain: string;
  full: string;
}

export const getEmailSuggestion = (email: string): EmailSuggestion | null => {
  let emailSuggestion: EmailSuggestion = { address: '', domain: '', full: '' };

  Mailcheck.run({
    domains,
    secondLevelDomains,
    email,
    suggested: (suggestion: EmailSuggestion) => {
      emailSuggestion = suggestion;
    },
    empty: () => {
      // this block runs when no typo is detected
    },
  });
  const hasSuggestion = Object.values(emailSuggestion).some(
    (val: string) => val.length > 0,
  );
  return hasSuggestion ? emailSuggestion : null;
};
